import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import SEO, { softwareAppSchema } from '../Components/SEO';

const PageStyles = () => (
  <style>{`
    :root {
      scroll-behavior: smooth;
    }
    body {
        font-family: 'Inter', sans-serif;
        background-color: #050505;
        color: #e5e7eb;
        line-height: 1.6;
        overflow-x: hidden;
    }
    /* --- Enhanced Animations --- */
    @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
    }
    .animate-on-scroll {
        opacity: 0; /* Hidden initially */
    }
    .animate-visible {
        animation: fadeInUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    }
    /* Stagger delays */
    .delay-100 { animation-delay: 0.1s; }
    .delay-200 { animation-delay: 0.2s; }
    .delay-300 { animation-delay: 0.3s; }

    /* --- Backgrounds --- */
    .hero-bg {
        background-color: #050505;
        background-image: 
            radial-gradient(circle at 15% 50%, rgba(56, 189, 248, 0.08) 0%, transparent 40%),
            radial-gradient(circle at 85% 30%, rgba(129, 140, 248, 0.08) 0%, transparent 40%),
            linear-gradient(#0a0a0a 1px, transparent 1px),
            linear-gradient(90deg, #0a0a0a 1px, transparent 1px);
        background-size: 100% 100%, 100% 100%, 40px 40px, 40px 40px;
    }
    .gradient-text {
        background: linear-gradient(135deg, #38bdf8, #818cf8, #c084fc);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
    
    /* --- UI Components --- */
    .btn-primary {
        background: linear-gradient(135deg, #0ea5e9, #4f46e5);
        position: relative;
        overflow: hidden;
        transition: all 0.3s ease;
        box-shadow: 0 4px 20px rgba(14, 165, 233, 0.3);
    }
    .btn-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 30px rgba(14, 165, 233, 0.5);
    }
    .btn-secondary {
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        transition: all 0.3s ease;
    }
    .btn-secondary:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(14, 165, 233, 0.5);
    }
    .feature-card {
        background: linear-gradient(180deg, rgba(30, 41, 59, 0.2), rgba(15, 23, 42, 0.4));
        border: 1px solid rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(12px);
        transition: all 0.4s ease;
    }
    .feature-card:hover {
        transform: translateY(-5px);
        border-color: rgba(56, 189, 248, 0.3);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    }
    .hotkey-card {
        background: rgba(15, 23, 42, 0.6);
        border-left: 3px solid #0ea5e9;
        transition: all 0.3s;
    }
    .hotkey-card:hover {
        background: rgba(30, 41, 59, 0.8);
        padding-left: 1.25rem;
    }
    
    /* --- Activation Modal --- */
    .modal-backdrop {
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(12px);
    }
    .activation-paper {
        background: #ffffff;
        color: #1a1a2e;
        box-shadow: 0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.05);
    }
    .activation-paper input[type=text],
    .activation-paper input[type=email],
    .activation-paper input[type=tel],
    .activation-paper input[type=number] {
        transition: border-color 0.2s, box-shadow 0.2s;
    }
    .activation-paper input:focus {
        border-color: #6366f1 !important;
        box-shadow: 0 0 0 3px rgba(99,102,241,0.1) !important;
    }
    @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
    .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
  `}</style>
);

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://gateway.checkscript.online';

const ActivationModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [deviceId, setDeviceId] = useState('');
  const [lookupStatus, setLookupStatus] = useState('idle'); // idle | loading | found | not-found | error
  const [existingUser, setExistingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    wantQuestions: false,
    wantPremium: false,
    wantRemote: false,
    questions: 25,
    remoteDuration: '2h',
  });
  const [totalPrice, setTotalPrice] = useState(0);
  const [submissionStatus, setSubmissionStatus] = useState(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState('idle'); // idle | loading | applied | error
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState('');
  const [promoInfo, setPromoInfo] = useState(null); // { discount_type, discount_value }

  // Step 3: QR payment state
  const [orderData, setOrderData] = useState(null); // { orderId, amount, upiLink, expiresAt }
  const [paymentStatus, setPaymentStatus] = useState('waiting'); // waiting | polling | success | failed | expired
  const [timeLeft, setTimeLeft] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [lookupTurnstileToken, setLookupTurnstileToken] = useState(null);
  const [lookupSignature, setLookupSignature] = useState(null);
  const [lookupCaptchaError, setLookupCaptchaError] = useState(false);
  const [lookupCaptchaLoading, setLookupCaptchaLoading] = useState(false);
  const [lookupCaptchaLoadFailed, setLookupCaptchaLoadFailed] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const turnstileRef = useRef(null);
  const lookupTurnstileRef = useRef(null);

  const TURNSTILE_SITE_KEY = '0x4AAAAAACogxPVIgu5ZqAFu';

  const REMOTE_TIERS = {
    '1h':  { label: '1 Hour',   price: 99,  desc: 'Quick fix or demo'      },
    '2h':  { label: '2 Hours',  price: 179, desc: 'Most popular'           },
    '4h':  { label: '4 Hours',  price: 299, desc: 'Extended session'       },
    '8h':  { label: '8 Hours',  price: 499, desc: 'Full day'               },
    '1d':  { label: '24 Hours', price: 699, desc: 'Round the clock'        },
    '3d':  { label: '3 Days',   price: 1499, desc: 'Multi-day project'     },
    '7d':  { label: '7 Days',   price: 2499, desc: 'Full week'             },
  };

  const lookupValue = deviceId.trim();
  const isRegistrationActivation = /^\d+$/.test(lookupValue);
  const isExistingUser = !!existingUser;
  const questionOnlyMode = isRegistrationActivation;
  const isPremiumEligible = !questionOnlyMode && (isExistingUser || (formData.wantQuestions && (parseInt(formData.questions, 10) || 0) >= 36));
  const anySelected = formData.wantQuestions || (!questionOnlyMode && (formData.wantPremium || formData.wantRemote));
  const rdpExpireDate = isExistingUser && existingUser.RDP_expire ? new Date(existingUser.RDP_expire) : null;
  const isRdpExpired = rdpExpireDate ? rdpExpireDate < new Date() : true;

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setStep(1); setDeviceId(''); setLookupStatus('idle'); setExistingUser(null);
        setSubmissionStatus(null); setOrderData(null); setPaymentStatus('waiting'); setTimeLeft(0);
        setLookupTurnstileToken(null); setLookupSignature(null); setLookupCaptchaError(false);
        setLookupCaptchaLoading(false); setLookupCaptchaLoadFailed(false);
        setPromoCode(''); setPromoStatus('idle'); setPromoDiscount(0); setPromoMessage(''); setPromoInfo(null);
        setFormData({ name: '', email: '', mobile: '', wantQuestions: false, wantPremium: false, wantRemote: false, questions: 25, remoteDuration: '2h' });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!questionOnlyMode) return;
    setFormData(prev => ({
      ...prev,
      wantQuestions: true,
      wantPremium: false,
      wantRemote: false,
    }));
  }, [questionOnlyMode]);

  // Cleanup polling & timer on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Load Turnstile script once
  useEffect(() => {
    if (document.getElementById('cf-turnstile-script')) return;
    const s = document.createElement('script');
    s.id = 'cf-turnstile-script';
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    document.head.appendChild(s);
  }, []);

  // Render Turnstile widget when step 2 is shown
  useEffect(() => {
    if (!isOpen || step !== 2 || !turnstileRef.current) return;
    setTurnstileToken(null);
    let widgetId = null;
    const mount = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      turnstileRef.current.innerHTML = '';
      widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
        'error-callback': () => setTurnstileToken(null),
        appearance: 'always',
        execution: 'render',
        theme: 'light',
        size: 'normal',
      });
    };

    if (window.turnstile) {
      mount();
    } else {
      let attempts = 0;
      const retry = setInterval(() => {
        attempts += 1;
        if (window.turnstile) {
          clearInterval(retry);
          mount();
        } else if (attempts >= 30) {
          clearInterval(retry);
        }
      }, 200);
      return () => clearInterval(retry);
    }

    return () => {
      if (widgetId !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch (e) {}
      }
    };
  }, [step, isOpen]);

  // Render lookup Turnstile widget when step 1 is shown
  useEffect(() => {
    if (!isOpen || step !== 1 || !lookupTurnstileRef.current) return;
    setLookupCaptchaLoading(true);
    setLookupCaptchaLoadFailed(false);
    setLookupTurnstileToken(null);
    let widgetId = null;
    const mount = () => {
      if (!window.turnstile || !lookupTurnstileRef.current) return;
      lookupTurnstileRef.current.innerHTML = '';
      widgetId = window.turnstile.render(lookupTurnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => {
          setLookupTurnstileToken(token);
          setLookupCaptchaError(false);
          setLookupCaptchaLoading(false);
        },
        'expired-callback': () => {
          setLookupTurnstileToken(null);
        },
        'error-callback': () => {
          setLookupTurnstileToken(null);
          setLookupCaptchaLoadFailed(true);
          setLookupCaptchaLoading(false);
        },
        appearance: 'always',
        execution: 'render',
        theme: 'light',
        size: 'normal',
      });
    };
    if (window.turnstile) {
      mount();
      return;
    }

    let attempts = 0;
    const maxAttempts = 30;
    const retry = setInterval(() => {
      attempts += 1;
      if (window.turnstile) {
        clearInterval(retry);
        mount();
      } else if (attempts >= maxAttempts) {
        clearInterval(retry);
        setLookupCaptchaLoadFailed(true);
        setLookupCaptchaLoading(false);
      }
    }, 200);

    return () => {
      clearInterval(retry);
      if (widgetId !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch (e) {}
      }
    };
  }, [step, isOpen]);

  // Price calc
  useEffect(() => {
    let price = 0;
    if (formData.wantQuestions) {
      const n = parseInt(formData.questions, 10) || 0;
      price += n * (n > 40 ? 10.00 : 12.59);
    }
    if (formData.wantPremium && isPremiumEligible) price += 199;
    if (formData.wantRemote) price += REMOTE_TIERS[formData.remoteDuration]?.price || 99;
    setTotalPrice(price);
    // Recalculate promo discount when price changes
    if (promoInfo && price > 0) {
      const d = promoInfo.discount_type === 'PERCENTAGE'
        ? Math.round(price * promoInfo.discount_value / 100 * 100) / 100
        : Math.min(promoInfo.discount_value, price);
      setPromoDiscount(d);
    } else {
      setPromoDiscount(0);
    }
  }, [formData, isPremiumEligible, promoInfo]);

  // Lookup device
  const lookupDevice = async () => {
    if (!lookupValue) return;
    if (!lookupTurnstileToken) {
      setLookupCaptchaError(true);
      return;
    }
    setLookupCaptchaError(false);
    setLookupStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/v1/users/details/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mac_address: lookupValue,
          registration_number: isRegistrationActivation ? lookupValue : undefined,
          lookupTurnstileToken,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success && data.lookup_signature && data.lookup_status === 'found' && data.user) {
        setLookupSignature(data.lookup_signature);
        setExistingUser(data.user);
        setFormData(prev => ({ ...prev, name: data.user.name || prev.name }));
        setLookupStatus('found');
      } else if (res.ok && data.success && data.lookup_signature && data.lookup_status === 'not-found') {
        setLookupSignature(data.lookup_signature);
        setExistingUser(null);
        setLookupStatus('not-found');
      } else {
        setLookupSignature(null);
        setExistingUser(null);
        setLookupStatus('error');
      }
    } catch {
      setLookupSignature(null);
      setLookupStatus('error');
    }
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [id]: type === 'checkbox' ? checked : value };
      if (id === 'questions' && parseInt(value, 10) < 36 && !isExistingUser) next.wantPremium = false;
      if (id === 'wantQuestions' && !checked && !isExistingUser) next.wantPremium = false;
      return next;
    });
  };

  const toggleFeature = (id) => {
    if (questionOnlyMode && (id === 'wantPremium' || id === 'wantRemote')) return;
    if (id === 'wantPremium' && !isPremiumEligible) return;
    setFormData(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Promo code validation
  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoStatus('loading');
    setPromoMessage('');
    try {
      const res = await fetch(`${API_URL}/api/order/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode.trim(), amount: totalPrice }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPromoInfo({ discount_type: data.discount_type, discount_value: data.discount_value });
        setPromoDiscount(data.discount);
        setPromoStatus('applied');
        setPromoMessage(`${data.discount_type === 'PERCENTAGE' ? data.discount_value + '%' : '₹' + data.discount_value} off applied!`);
      } else {
        setPromoInfo(null);
        setPromoDiscount(0);
        setPromoStatus('error');
        setPromoMessage(data.message || 'Invalid code');
      }
    } catch {
      setPromoInfo(null);
      setPromoDiscount(0);
      setPromoStatus('error');
      setPromoMessage('Could not validate code');
    }
  };

  const clearPromo = () => {
    setPromoCode('');
    setPromoStatus('idle');
    setPromoDiscount(0);
    setPromoMessage('');
    setPromoInfo(null);
  };

  // Poll payment status from server
  const startPolling = useCallback((orderId) => {
    setPaymentStatus('polling');
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/order/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId }),
        });
        const data = await res.json();
        if (data.success) {
          if (data.status === 'TXN_SUCCESS') {
            setPaymentStatus('success');
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
          } else if (data.status === 'TXN_FAILURE') {
            setPaymentStatus('failed');
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
          } else if (data.status === 'EXPIRED') {
            setPaymentStatus('expired');
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
          }
        }
      } catch { /* retry next tick */ }
    }, 4000); // poll every 4 seconds
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!anySelected) return;
    if (!turnstileToken) {
      setSubmissionStatus('captcha');
      return;
    }
    setSubmissionStatus('submitting');
    try {
      const res = await fetch(`${API_URL}/api/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          deviceId: isRegistrationActivation ? '' : lookupValue,
          registrationNumber: isRegistrationActivation ? lookupValue : undefined,
          activationMode: isRegistrationActivation ? 'registration' : 'device',
          lookupValue,
          lookupSignature,
          wantQuestions: formData.wantQuestions,
          wantPremium: questionOnlyMode ? false : formData.wantPremium,
          wantRemote: questionOnlyMode ? false : formData.wantRemote,
          questions: formData.questions,
          remoteDuration: questionOnlyMode ? undefined : formData.remoteDuration,
          turnstileToken,
          promoCode: promoStatus === 'applied' ? promoCode.trim() : undefined,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success && data.upiLink) {
        setSubmissionStatus(null);
        setOrderData(data);
        setPaymentStatus('waiting');

        // Calculate countdown seconds
        const expiryMs = new Date(data.expiresAt).getTime() - Date.now();
        setTimeLeft(Math.max(0, Math.floor(expiryMs / 1000)));

        setStep(3);

        // Start countdown timer
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              setPaymentStatus(ps => ps === 'waiting' || ps === 'polling' ? 'expired' : ps);
              if (pollRef.current) clearInterval(pollRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Start polling server for payment confirmation
        startPolling(data.orderId);
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch {
      setSubmissionStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div className="activation-paper rounded-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* ─── Header ─── */}
        <div className="px-7 pt-6 pb-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {step === 1 ? 'Activate CheckScript' : step === 2 ? 'Build your plan' : 'Complete payment'}
              </h2>
              <p className="text-[13px] text-gray-400 mt-0.5">
                {step === 1
                  ? 'Enter your device ID or registration number to get started'
                  : step === 2
                    ? (questionOnlyMode ? 'Registration activation supports question quota only' : 'Pick what you need — everything is optional')
                    : 'Scan QR with any UPI app'}
              </p>
            </div>
            {step !== 3 && (
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
          {/* Progress */}
          <div className="flex gap-1.5 mt-4">
            <div className={`h-[3px] rounded-full flex-1 transition-colors ${step >= 1 ? 'bg-indigo-500' : 'bg-gray-200'}`} />
            <div className={`h-[3px] rounded-full flex-1 transition-colors ${step >= 2 ? 'bg-indigo-500' : 'bg-gray-200'}`} />
            <div className={`h-[3px] rounded-full flex-1 transition-colors ${step >= 3 ? 'bg-indigo-500' : 'bg-gray-200'}`} />
          </div>
        </div>

        {/* ─── Body ─── */}
        <div className="px-7 py-6 max-h-[62vh] overflow-y-auto">

          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Device ID / Registration Number</label>
                <div className="flex gap-2">
                  <input type="text" value={deviceId} onChange={e => { setDeviceId(e.target.value); setLookupStatus('idle'); setExistingUser(null); setLookupSignature(null); setLookupCaptchaError(false); }}
                    className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono outline-none"
                    placeholder="Paste your device ID or enter registration number" />
                  <button type="button" onClick={lookupDevice} disabled={!lookupValue || lookupStatus === 'loading'}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
                    {lookupStatus === 'loading' ? 'Checking...' : 'Look up'}
                  </button>
                </div>
                <div className="mt-3 flex justify-center">
                  <div ref={lookupTurnstileRef} />
                </div>
                {lookupCaptchaLoading && !lookupTurnstileToken && (
                  <p className="mt-2 text-xs text-gray-500 text-center">Loading captcha...</p>
                )}
                {lookupCaptchaLoadFailed && !lookupTurnstileToken && (
                  <p className="mt-2 text-xs text-red-500 text-center">Captcha failed to load. Refresh the page and try again.</p>
                )}
                {lookupCaptchaError && (
                  <p className="mt-2 text-xs text-amber-600 text-center">Please complete captcha before lookup.</p>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  Numeric entries are treated as registration-based activation and will unlock question quota only.
                </p>
              </div>

              {/* Status: found */}
              {lookupStatus === 'found' && existingUser && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-sm font-semibold text-emerald-800">{questionOnlyMode ? 'Registration recognized' : 'Device recognized'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                    <div><span className="text-gray-500">Name</span><p className="font-medium text-gray-900">{existingUser.name || '—'}</p></div>
                    <div><span className="text-gray-500">{questionOnlyMode ? 'Registration' : 'Device'}</span><p className="font-medium text-gray-900 font-mono text-xs">{existingUser.mac_address}</p></div>
                    <div><span className="text-gray-500">Questions left</span><p className="font-medium text-gray-900">{(existingUser.api_limit - existingUser.api_used)} / {existingUser.api_limit}</p></div>
                    <div><span className="text-gray-500">Member since</span><p className="font-medium text-gray-900">{new Date(existingUser.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</p></div>
                  </div>
                  {!questionOnlyMode && (
                      <div className="col-span-2 mt-1">
                        <span className="text-gray-500">Remote Access: </span>
                        <span className={`font-medium ${existingUser.RDP_status === 'active' && !isRdpExpired ? 'text-emerald-600' : isRdpExpired ? 'text-red-500' : 'text-gray-900'}`}>
                          {existingUser.RDP_status === 'active' && rdpExpireDate
                            ? !isRdpExpired
                              ? `Active until ${rdpExpireDate.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}`
                              : 'Expired'
                            : 'Inactive'}
                        </span>
                      </div>
                    )}
                </div>
              )}

              {/* Status: new user */}
              {lookupStatus === 'not-found' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">{questionOnlyMode ? 'Registration not found' : 'New device'}</p>
                    <p className="text-xs text-amber-700 mt-0.5">{questionOnlyMode ? 'You can still continue. Registration-based activation supports question quota only.' : "This device hasn't been activated yet. You'll set up your account in the next step."}</p>
                  </div>
                </div>
              )}

              {lookupStatus === 'error' && (
                <p className="text-sm text-red-600">Unable to reach server. Please check your connection.</p>
              )}

              {/* Instructions for finding ID */}
              <details className="group">
                <summary className="text-[13px] text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                  Need help finding the right ID?
                </summary>
                <ol className="mt-2 pl-5 list-decimal text-[13px] text-gray-500 space-y-1">
                  <li>Open <strong className="text-gray-700">CheckScript v19.1.0</strong></li>
                  <li>The device ID is shown on the startup screen</li>
                  <li>If you already have a registration number, enter that instead</li>
                  <li>Registration-based activation enables question quota only</li>
                </ol>
              </details>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <form id="activation-form" className="space-y-5" onSubmit={handleSubmit}>

              {questionOnlyMode && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-semibold text-amber-800">Registration-based activation</p>
                  <p className="mt-1 text-xs text-amber-700">This flow supports question quota only. Premium features and remote support are available only with device-based activation.</p>
                </div>
              )}

              {/* Returning user banner */}
              {isExistingUser && (
                <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-bold">
                    {existingUser.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-indigo-900">Welcome back, {existingUser.name}</p>
                    <p className="text-xs text-indigo-600">{existingUser.api_limit - existingUser.api_used} questions remaining</p>
                  </div>
                  {!questionOnlyMode && existingUser.RDP_status === 'active' && rdpExpireDate && (
                    <div className={`pt-2 border-t text-[11px] font-medium ${!isRdpExpired ? 'border-indigo-200/50 text-indigo-700' : 'border-red-200/50 text-red-600'}`}>
                      {!isRdpExpired
                        ? `🖥️ Remote Plan active until ${rdpExpireDate.toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                        : `⚠️ Remote Plan expired on ${rdpExpireDate.toLocaleString('en-IN', { month: 'short', day: 'numeric' })}`}
                    </div>
                  )}
                </div>
              )}

              {/* Personal info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input type="text" id="name" value={formData.name} onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none" placeholder="Your name" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                  <input type="tel" id="mobile" value={formData.mobile} onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none" placeholder="+91..." required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" id="email" value={formData.email} onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none" placeholder="you@example.com" required />
              </div>

              <div className="h-px bg-gray-100" />

              {/* ─ Feature 1: Questions ─ */}
              <div onClick={() => toggleFeature('wantQuestions')}
                className={`rounded-xl border p-4 cursor-pointer transition-all
                  ${formData.wantQuestions ? 'border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📝</span>
                    <div>
                      <p className={`text-sm font-semibold ${formData.wantQuestions ? 'text-indigo-900' : 'text-gray-800'}`}>Question Quota</p>
                      <p className="text-xs text-gray-400">Add more question quota to your account</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-400">from ₹10/q</span>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                      ${formData.wantQuestions ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'}`}>
                      {formData.wantQuestions && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                </div>
                {formData.wantQuestions && (
                  <div className="mt-3 pt-3 border-t border-indigo-100" onClick={e => e.stopPropagation()}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">How many questions?</label>
                    <input type="number" id="questions" value={formData.questions} onChange={handleChange} min="25"
                      className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg text-sm outline-none" />
                    <p className="text-xs text-gray-400 mt-1">
                      {parseInt(formData.questions, 10) > 40 ? 'Bulk rate: ₹10.00 per question' : '₹12.59/q — order 40+ for ₹10/q'}
                    </p>
                  </div>
                )}
              </div>

              {!questionOnlyMode && (
                <>
                  {/* ─ Feature 2: Premium ─ */}
                  <div onClick={() => isPremiumEligible && toggleFeature('wantPremium')}
                    className={`rounded-xl border p-4 transition-all relative
                      ${!isPremiumEligible ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed' :
                        formData.wantPremium ? 'border-violet-300 bg-violet-50/60 ring-1 ring-violet-200 cursor-pointer' : 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">⚡</span>
                        <div>
                          <p className={`text-sm font-semibold ${formData.wantPremium ? 'text-violet-900' : 'text-gray-800'}`}>Premium Features</p>
                          <p className="text-xs text-gray-400">
                            {isPremiumEligible
                              ? 'Coding mode, stealth, network bypass'
                              : isExistingUser ? 'Available for your account' : 'Requires 36+ questions or existing account'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-400">+₹199</span>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                          ${formData.wantPremium ? 'bg-violet-500 border-violet-500' : 'border-gray-300'}`}>
                          {formData.wantPremium && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                        </div>
                      </div>
                    </div>
                    {formData.wantPremium && isPremiumEligible && (
                      <div className="mt-3 pt-3 border-t border-violet-100 text-xs text-violet-700 space-y-1.5">
                        <p>• Auto-typing engine — 15+ languages, human-like speed</p>
                        <p>• Process hiding & anti-fingerprinting</p>
                        <p>• Enterprise firewall bypass & DNS-over-HTTPS</p>
                      </div>
                    )}
                  </div>

                  {/* ─ Feature 3: Remote Support ─ */}
                  <div onClick={() => toggleFeature('wantRemote')}
                    className={`rounded-xl border p-4 cursor-pointer transition-all relative
                      ${formData.wantRemote ? 'border-cyan-300 bg-cyan-50/60 ring-1 ring-cyan-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                    {!formData.wantRemote && <span className="absolute -top-2 right-3 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-cyan-100 text-cyan-700">New</span>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🖥️</span>
                        <div>
                          <p className={`text-sm font-semibold ${formData.wantRemote ? 'text-cyan-900' : 'text-gray-800'}`}>Remote Support</p>
                          <p className="text-xs text-gray-400">Live screen sharing & remote control</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-400">from ₹99</span>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                          ${formData.wantRemote ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300'}`}>
                          {formData.wantRemote && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                        </div>
                      </div>
                    </div>
                    {formData.wantRemote && (
                      <div className="mt-3 pt-3 border-t border-cyan-100" onClick={e => e.stopPropagation()}>
                        <p className="text-xs font-medium text-gray-500 mb-2">How long do you need access?</p>
                        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-7">
                          {Object.entries(REMOTE_TIERS).map(([key, tier]) => (
                            <button key={key} type="button" onClick={() => setFormData(prev => ({ ...prev, remoteDuration: key }))}
                              className={`py-2 rounded-lg text-center text-[11px] transition-all
                                ${formData.remoteDuration === key
                                  ? 'bg-cyan-600 text-white font-semibold shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                              <div className="font-medium leading-tight">{tier.label}</div>
                              <div className={`leading-tight ${formData.remoteDuration === key ? 'text-cyan-100' : 'text-gray-400'}`}>₹{tier.price}</div>
                            </button>
                          ))}
                        </div>
                       <p className="text-[11px] text-gray-400 mt-2">
                          {existingUser && !isRdpExpired 
                            ? 'Time will be added to your existing plan' 
                            : `Expires ${REMOTE_TIERS[formData.remoteDuration].label.toLowerCase()} after activation`}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ─ Summary ─ */}
              {anySelected ? (
                <div className="rounded-xl bg-gray-50 p-4 space-y-2">
                  {formData.wantQuestions && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{formData.questions} questions</span>
                      <span className="text-gray-800 font-medium">₹{(parseInt(formData.questions, 10) * (parseInt(formData.questions, 10) > 40 ? 10 : 12.59)).toFixed(2)}</span>
                    </div>
                  )}
                  {formData.wantPremium && isPremiumEligible && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Premium</span>
                      <span className="text-gray-800 font-medium">₹199.00</span>
                    </div>
                  )}
                  {formData.wantRemote && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Remote — {REMOTE_TIERS[formData.remoteDuration].label}</span>
                      <span className="text-gray-800 font-medium">₹{REMOTE_TIERS[formData.remoteDuration].price.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="text-sm font-semibold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-gray-900">₹{totalPrice.toFixed(2)}</span>
                  </div>
                  {promoDiscount > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Promo discount</span>
                        <span className="font-medium">−₹{promoDiscount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-green-100">
                        <span className="text-sm font-semibold text-gray-900">You pay</span>
                        <span className="text-xl font-bold text-green-700">₹{Math.max(1, totalPrice - promoDiscount).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-center text-sm text-gray-300 py-2">Select at least one option above</p>
              )}

              {/* Promo Code */}
              {anySelected && (
                <div className="space-y-2">
                  {promoStatus !== 'applied' ? (
                    <div className="flex gap-2">
                      <input type="text" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Promo code" maxLength={20}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-800 placeholder-gray-400 font-mono tracking-wider"
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), applyPromo())} />
                      <button type="button" onClick={applyPromo} disabled={!promoCode.trim() || promoStatus === 'loading'}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                          ${promoCode.trim() ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                        {promoStatus === 'loading' ? '...' : 'Apply'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 text-sm">✓</span>
                        <span className="text-sm font-mono font-medium text-green-800">{promoCode}</span>
                        <span className="text-xs text-green-600">{promoMessage}</span>
                      </div>
                      <button type="button" onClick={clearPromo} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Remove</button>
                    </div>
                  )}
                  {promoStatus === 'error' && promoMessage && (
                    <p className="text-xs text-red-500 pl-1">{promoMessage}</p>
                  )}
                </div>
              )}

              {/* Cloudflare Turnstile */}
              <div className="flex justify-center">
                <div ref={turnstileRef} />
              </div>
            </form>
          )}

          {/* Step 3 — QR Payment */}
          {step === 3 && orderData && (() => {
            const mins = Math.floor(timeLeft / 60);
            const secs = timeLeft % 60;
            const pct = Math.max(0, (timeLeft / (15 * 60)) * 100);
            const isTerminal = paymentStatus === 'success' || paymentStatus === 'failed' || paymentStatus === 'expired';

            return (
              <div className="flex flex-col items-center text-center space-y-5">

                {/* Order ID badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
                  <span className="text-[11px] text-gray-400">Order</span>
                  <span className="text-xs font-mono font-semibold text-gray-700">{orderData.orderId}</span>
                </div>

                {/* Payment status indicator */}
                {paymentStatus === 'success' ? (
                  <div className="space-y-3 py-4 animate-fadeIn">
                    <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-emerald-800">Payment successful!</p>
                      <p className="text-sm text-gray-500 mt-1">₹{orderData.amount} received. Your features are being activated.</p>
                    </div>
                  </div>
                ) : paymentStatus === 'failed' ? (
                  <div className="space-y-3 py-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-red-700">Payment failed</p>
                      <p className="text-sm text-gray-500 mt-1">The transaction was declined. Please try again.</p>
                    </div>
                  </div>
                ) : paymentStatus === 'expired' ? (
                  <div className="space-y-3 py-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                      <span className="text-2xl">⏱</span>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-amber-700">Order expired</p>
                      <p className="text-sm text-gray-500 mt-1">Payment wasn't received in time. Go back to create a new order.</p>
                    </div>
                  </div>
                ) : (
                  /* Waiting / Polling — show QR */
                  <>
                    {/* QR Code */}
                    <div className="relative p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
                      <QRCodeSVG
                        value={orderData.upiLink}
                        size={200}
                        level="M"
                        includeMargin={false}
                        bgColor="#ffffff"
                        fgColor="#1a1a2e"
                      />
                      {/* Pulsing ring while waiting */}
                      <div className="absolute inset-0 rounded-2xl border-2 border-indigo-400 animate-pulse pointer-events-none opacity-40" />
                    </div>

                    <div>
                      <p className="text-2xl font-bold text-gray-900">₹{orderData.amount}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Scan with any UPI app — GPay, PhonePe, Paytm</p>
                    </div>

                    {/* Timer bar */}
                    <div className="w-full max-w-xs space-y-1.5">
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000 ease-linear"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: pct > 30 ? '#6366f1' : pct > 10 ? '#f59e0b' : '#ef4444'
                          }} />
                      </div>
                      <p className={`text-xs font-medium tabular-nums ${pct > 30 ? 'text-gray-400' : pct > 10 ? 'text-amber-600' : 'text-red-500'}`}>
                        {mins}:{secs.toString().padStart(2, '0')} remaining
                      </p>
                    </div>

                    {/* Mobile: open UPI app directly */}
                    <a href={orderData.upiLink}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors sm:hidden">
                      Open UPI App
                    </a>

                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                      Listening for payment confirmation...
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {step !== 3 && submissionStatus === 'captcha' && <div className="mt-4 p-3 bg-amber-50 text-amber-600 rounded-lg text-sm font-medium text-center">Please complete the captcha verification above.</div>}
          {step !== 3 && submissionStatus === 'error' && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium text-center">Something went wrong. Please try again.</div>}
          {step !== 3 && submissionStatus === 'submitting' && <div className="mt-4 p-3 bg-gray-50 text-gray-500 rounded-lg text-sm font-medium text-center">Creating order...</div>}
        </div>

        {/* ─── Footer ─── */}
        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50/50">
          {step === 1 ? (
            <button onClick={() => (lookupStatus === 'found' || lookupStatus === 'not-found') ? setStep(2) : lookupDevice()}
              disabled={!lookupValue || lookupStatus === 'loading' || ((lookupStatus === 'found' || lookupStatus === 'not-found') && !lookupSignature)}
              className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors
                ${(lookupValue && !(((lookupStatus === 'found' || lookupStatus === 'not-found') && !lookupSignature))) ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              {lookupStatus === 'loading' ? 'Checking...' : lookupStatus === 'found' || lookupStatus === 'not-found' ? 'Continue' : 'Look up ID'}
            </button>
          ) : step === 2 ? (
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep(1)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors">Back</button>
              <button type="submit" form="activation-form" disabled={!anySelected || !turnstileToken || submissionStatus === 'submitting'}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors
                  ${anySelected ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                {submissionStatus === 'submitting' ? 'Creating order...' : anySelected ? `Pay ₹${Math.max(1, totalPrice - promoDiscount).toFixed(2)}` : 'Select a feature'}
              </button>
            </div>
          ) : (
            /* Step 3 footer */
            <div className="flex gap-2">
              {(paymentStatus === 'failed' || paymentStatus === 'expired') && (
                <button type="button" onClick={() => { setStep(2); setSubmissionStatus(null); setOrderData(null); setPaymentStatus('waiting'); }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                  Try again
                </button>
              )}
              {paymentStatus === 'success' && (
                <button type="button" onClick={onClose}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                  Done
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CheckScriptHome = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Enhanced Scroll Animation Logic
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const delay = parseInt(entry.target.dataset.delay || 0, 10);
                entry.target.style.animationDelay = `${delay}ms`;
                entry.target.classList.add('animate-visible');
            }
        });
    }, { threshold: 0.1, rootMargin: "0px 0px -50px 0px" });

    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEO 
        title="CheckScript AI v19 — Ultimate Assessment & Remote Desktop Tool"
        description="CheckScript AI is the world's most advanced AI study assistant. Features God Mode screen capture, local Neural Driver AI, and sub-100ms Premium Remote Desktop."
        keywords="AI assessment tool, AI exam helper, advanced remote desktop, Neural Driver, CheckScript AI"
        path="/"
        structuredData={{
          ...softwareAppSchema(),
          "offers": {
            "@type": "Offer",
            "price": "10.00",
            "priceCurrency": "INR"
          }
        }}
      />
      <PageStyles />
      <ActivationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center font-bold text-white">CS</div>
                <h1 className="text-xl font-bold text-white tracking-tight">CheckScript <span className="text-sky-500">v19.1.0</span></h1>
            </div>
            <nav className="hidden md:flex space-x-6 items-center">
                <a href="#downloads" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Downloads</a>
                <a href="#neural-driver" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Neural Driver</a>
                <a href="#remote-desktop" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Remote</a>
                <a href="#hotkeys" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Hotkeys</a>
                <a href="#partners" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Partners</a>
                <button onClick={() => window.location.href = "https://t.me/checkscriptsupport"}  className="text-sm font-bold text-sky-400 hover:text-sky-300">Activate Premium</button>
            </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center justify-center pt-24 px-4 pb-12">
        <div className="container mx-auto max-w-5xl text-center relative z-10">
            <div className="animate-on-scroll" data-delay="100">
                <span className="inline-block py-1 px-3 rounded-full bg-sky-900/30 border border-sky-500/30 text-sky-300 text-xs font-bold tracking-wider mb-6">
                    ⚡ UPDATED: v19.1.0 "GOD MODE" RELEASED
                </span>
                <h2 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                    The Smartest Way to <br/>
                    <span className="gradient-text">Master Assessments.</span>
                </h2>
                <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
                    A powerful AI assistant for students and developers. Solve mock tests, debug code, and capture study material from any application—even those with black-screen restrictions.
                </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 animate-on-scroll" data-delay="200" id="downloads">
                <a href="https://github.com/Checkscript/CheckScript-Releases/releases/download/v19.1.0/CheckScript-AI-Helper-Web-Setup-19.1.0.exe"
                   className="btn-primary text-white font-bold py-4 px-8 rounded-2xl flex items-center justify-center gap-3 relative overflow-visible">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/></svg>
                    <div className="text-left">
                        <div className="text-xs opacity-80 font-normal">Most Advanced Version</div>
                        <div>Download CheckScript v19.1.0</div>
                    </div>
                </a>
                <button onClick={() => window.location.href = "https://t.me/checkscriptsupport"} className="btn-secondary text-white font-bold py-4 px-8 rounded-2xl flex items-center justify-center gap-3">
                    <span className="text-2xl">⚡</span>
                    <div className="text-left">
                        <div className="text-xs opacity-80 font-normal">Get Access</div>
                        <div>Activate License</div>
                    </div>
                </button>
            </div>

            {/* Previous version link */}
            <div className="mt-4 text-center animate-on-scroll" data-delay="250">
                <a href="https://github.com/Checkscript/CheckScript-Releases/releases/download/v18.2.0/CheckScript-AI-Helper-Web-Setup-18.2.0.exe"
                   className="text-xs text-gray-500 hover:text-gray-400 transition-colors">
                   Need v18.2.0? Download it here →
                </a>
            </div>

            <div className="mt-12 pt-10 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-6 animate-on-scroll" data-delay="300">
                <div className="text-center">
                    <div className="text-2xl font-bold text-white">Native App</div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">Core Engine</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-white">15+ Langs</div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">Auto-Coding</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-white">God Mode</div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">Capture Any Screen</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-white">Hardware ID</div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">Device Lock</div>
                </div>
            </div>
        </div>
      </section>

      {/* Neural Driver Section */}
      <section id="neural-driver" className="py-24 bg-black relative overflow-hidden border-y border-white/5">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="animate-on-scroll" data-delay="100">
                    <div className="inline-block mb-4 px-3 py-1 bg-purple-900/30 border border-purple-500/30 rounded-lg text-purple-400 text-xs font-bold uppercase">
                        Enterprise Add-on
                    </div>
                    <h2 className="text-4xl font-bold text-white mb-6">Neural Driver Host</h2>
                    <p className="text-gray-400 text-lg mb-8">
                        Disconnect from the cloud. The Neural Driver is a standalone background service that runs Vision AI (Qwen2-VL) directly on your GPU.
                    </p>
                    <ul className="space-y-4 mb-8">
                        <li className="flex items-start gap-3">
                            <span className="text-green-400 text-xl">✓</span>
                            <span className="text-gray-300">Zero Latency: Direct local inference (30-50 tokens/sec) on supported hardware.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-green-400 text-xl">✓</span>
                            <span className="text-gray-300">100% Privacy: No questions or code are sent to external servers.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-green-400 text-xl">✓</span>
                            <span className="text-gray-300">Web Chat UI: Includes a secure built-in local interface.</span>
                        </li>
                    </ul>
                    <a href="" className="inline-flex items-center text-sky-400 hover:text-sky-300 font-bold border-b border-sky-400 pb-1">
                        Download Neural Driver Installer &rarr;
                    </a>
                </div>
                <div className="relative animate-on-scroll" data-delay="200">
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-sky-600 rounded-2xl blur opacity-30"></div>
                    <div className="relative bg-gray-900 rounded-xl border border-gray-800 p-6 shadow-2xl">
                         <div className="flex justify-between border-b border-gray-800 pb-4 mb-4">
                            <span className="text-green-500 font-mono text-xs">● DRIVER ONLINE</span>
                            <span className="text-gray-500 font-mono text-xs">SECURE LOCALHOST</span>
                         </div>
                         <div className="space-y-2 font-mono text-xs text-gray-400">
                            <p>[INIT] <span className="text-white">Loading Qwen2-VL-7B-Int4...</span></p>
                            <p>[GPU] <span className="text-sky-400">RTX 3060 Detected (12GB VRAM)</span></p>
                            <p>[IPC] <span className="text-white">Listening for CheckScript...</span></p>
                            <p className="text-purple-400 mt-4">&gt; Ready for inference.</p>
                         </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Remote Desktop Section */}
      <section id="remote-desktop" className="py-24 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-on-scroll" data-delay="100">
                <span className="inline-block py-1 px-3 rounded-full bg-orange-900/20 border border-orange-500/20 text-orange-300 text-xs font-bold tracking-wider mb-4">
                    NEW PRODUCT
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">CheckScript <span className="gradient-text">Remote</span></h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">Access any Windows desktop from your browser. No downloads, no plugins — just connect and control.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left — Feature list */}
                <div className="space-y-6 animate-on-scroll" data-delay="200">
                    <div className="feature-card p-5 rounded-2xl flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-orange-400 text-lg">🖥️</span>
                        </div>
                        <div>
                            <h3 className="text-white font-semibold mb-1">Unrestricted Screen Capture</h3>
                            <p className="text-gray-500 text-sm">Our native host captures any application — even fullscreen games, secure browsers, and DRM-protected content that other tools black out.</p>
                        </div>
                    </div>

                    <div className="feature-card p-5 rounded-2xl flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-blue-400 text-lg">🌍</span>
                        </div>
                        <div>
                            <h3 className="text-white font-semibold mb-1">Global Edge Network</h3>
                            <p className="text-gray-500 text-sm">Powered by Cloudflare's 300+ data centers. Sub-100ms latency with hardware-accelerated H.264 streaming at 30+ FPS.</p>
                        </div>
                    </div>

                    <div className="feature-card p-5 rounded-2xl flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-green-400 text-lg">🔒</span>
                        </div>
                        <div>
                            <h3 className="text-white font-semibold mb-1">End-to-End Encrypted</h3>
                            <p className="text-gray-500 text-sm">Per-device access codes with 256-bit TLS encryption. Direct peer-to-peer connections when possible — no data stored on servers.</p>
                        </div>
                    </div>

                    <div className="feature-card p-5 rounded-2xl flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-purple-400 text-lg">📱</span>
                        </div>
                        <div>
                            <h3 className="text-white font-semibold mb-1">Zero-Install Viewer</h3>
                            <p className="text-gray-500 text-sm">Open the link from any browser on any device — phone, tablet, laptop. Full mouse & keyboard control with no plugins or extensions needed.</p>
                        </div>
                    </div>
                </div>

                {/* Right — Product mockup + CTA */}
                <div className="animate-on-scroll" data-delay="300">
                    <div className="relative">
                        <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-blue-600 rounded-2xl blur opacity-20"></div>
                        <div className="relative bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden shadow-2xl">
                            {/* Fake browser chrome */}
                            <div className="bg-gray-950 px-4 py-3 flex items-center gap-3 border-b border-gray-800">
                                <div className="flex gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                                </div>
                                <div className="flex-1 bg-gray-900 rounded-lg px-3 py-1 text-xs text-gray-500 font-mono">
                                    checkscript.online/remote
                                </div>
                            </div>
                            {/* Simulated viewer UI */}
                            <div className="px-6 py-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                    <span className="text-green-400 text-xs font-medium">Live</span>
                                    <span className="text-gray-600 text-xs">|</span>
                                    <span className="text-gray-400 text-xs font-mono">28 fps · 1.2 Mbps · 42ms</span>
                                </div>
                                <div className="aspect-video bg-gradient-to-br from-blue-950 to-indigo-950 rounded-lg border border-gray-800 flex items-center justify-center mb-6">
                                    <div className="text-center">
                                        <div className="text-4xl mb-3">🖥️</div>
                                        <div className="text-gray-400 text-sm font-medium">Remote Desktop Stream</div>
                                        <div className="text-gray-600 text-xs mt-1">1920×1080 · H.264 Hardware Decoded</div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <span>🖱️ Mouse</span>
                                        <span>·</span>
                                        <span>⌨️ Keyboard</span>
                                        <span>·</span>
                                        <span>🖥️ Fullscreen</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-orange-400/50">
                                        <span className="text-[10px]">⚡</span>
                                        <span className="text-[10px]">Powered by Cloudflare</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <a href="/remote" className="btn-primary text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-3 flex-1 text-center">
                            <span className="text-xl">🚀</span>
                            <div className="text-left">
                                <div className="text-xs opacity-80 font-normal">Open Viewer</div>
                                <div>Connect to Remote Desktop</div>
                            </div>
                        </a>
                        <a href="https://github.com/Checkscript/CheckScript-Releases/releases/download/v19.1.0/CheckScript-AI-Helper-Web-Setup-19.1.0.exe" className="btn-secondary text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-3 flex-1 text-center">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/></svg>
                            <div className="text-left">
                                <div className="text-xs opacity-80 font-normal">For Host PC</div>
                                <div>Download Host Agent</div>
                            </div>
                        </a>
                    </div>

                    {/* Trust badges */}
                    <div className="mt-5 flex items-center justify-center gap-6 text-[11px] text-gray-600">
                        <span className="flex items-center gap-1">🔒 256-bit TLS</span>
                        <span className="flex items-center gap-1">⚡ Cloudflare Edge</span>
                        <span className="flex items-center gap-1">🖥️ H.264 GPU</span>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Hotkeys Section */}
      <section id="hotkeys" className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-16 animate-on-scroll" data-delay="100">
                <h2 className="text-3xl font-bold text-white mb-4">Master Control <span className="gradient-text">Hotkeys</span></h2>
                <p className="text-gray-400">Total control without ever leaving your exam window or IDE.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {/* MCQ Mode */}
                <div className="feature-card p-8 rounded-3xl animate-on-scroll" data-delay="200">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="text-2xl">🎯</span>
                        <h3 className="text-xl font-bold text-white">MCQ Mode</h3>
                        <span className="ml-auto text-[10px] font-bold tracking-wider bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-full">ACTIVE</span>
                    </div>
                    <p className="text-gray-500 text-xs mb-4">Scan your exam screen, mark answer regions, and get AI-powered answers instantly — all without leaving your window.</p>
                    <div className="space-y-3">
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">New Scan</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Start a fresh question scan</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-sky-400 font-mono font-bold text-sm">Alt + O</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Set Points</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Mark 4 corners of the question area</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-sky-400 font-mono font-bold text-sm">Alt + 1..4</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Set Region</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Define answer option boundaries</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-sky-400 font-mono font-bold text-sm">Alt + 5..6</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Capture</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Screenshot the marked region for AI</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-sky-400 font-mono font-bold text-sm">Alt + T</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Get Answer</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">AI analyzes and returns the answer</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-green-400 font-mono font-bold text-sm">Alt + Y</code>
                        </div>
                    </div>
                </div>

                {/* Coding Mode */}
                <div className="feature-card p-8 rounded-3xl animate-on-scroll relative overflow-hidden" data-delay="300">
                    {/* Upgrade overlay */}
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] z-10 rounded-3xl flex flex-col items-center justify-center">
                        <span className="text-3xl mb-3">🔧</span>
                        <span className="text-white font-bold text-lg mb-1">Under Upgrade</span>
                        <span className="text-gray-400 text-sm mb-4">We're rebuilding this mode from scratch</span>
                        <span className="text-[10px] font-bold tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/25 px-3 py-1 rounded-full animate-pulse">COMING SOON</span>
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                        <span className="text-2xl">💻</span>
                        <h3 className="text-xl font-bold text-white">Coding Mode</h3>
                        <span className="ml-auto text-[10px] font-bold tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full">UPGRADING</span>
                    </div>
                    <p className="text-gray-500 text-xs mb-4">Auto-type code solutions with human-like speed, pauses, and backspaces directly into your IDE or exam editor.</p>
                    <div className="space-y-3">
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Auto-Type Solution</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Types code naturally into your editor</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-purple-400 font-mono font-bold text-sm">Alt + Y</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Pause Typing</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Instantly pause the auto-typer</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-purple-400 font-mono font-bold text-sm">Ctrl+Alt+P</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Resume Typing</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Continue from where it paused</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-purple-400 font-mono font-bold text-sm">Ctrl+Alt+R</code>
                        </div>
                        <div className="hotkey-card p-3 rounded-lg flex justify-between items-center">
                            <div>
                                <span className="text-gray-300 text-sm font-medium">Capture & Analyze</span>
                                <p className="text-gray-600 text-[11px] mt-0.5">Screenshot code problem for AI</p>
                            </div>
                            <code className="bg-black/50 px-2.5 py-1 rounded text-sky-400 font-mono font-bold text-sm">Alt + T</code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* --- NEW: Vendor / Partner Section --- */}
      <section id="partners" className="py-24 relative overflow-hidden border-t border-white/5 bg-[#050505]">
          <div className="absolute inset-0">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none"></div>
          </div>
          <div className="container mx-auto px-4 max-w-6xl relative z-10">
              <div className="text-center mb-16 animate-on-scroll" data-delay="100">
                  <span className="inline-block py-1 px-3 rounded-full bg-emerald-900/20 border border-emerald-500/20 text-emerald-400 text-xs font-bold tracking-wider mb-4">
                      B2B WHOLESALE PROGRAM
                  </span>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Become an Authorized <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">Partner</span></h2>
                  <p className="text-gray-400 text-lg max-w-2xl mx-auto">Build your own business on our infrastructure. Buy licenses at massive wholesale discounts and keep 100% of your retail profits.</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                  <div className="feature-card p-8 rounded-3xl animate-on-scroll" data-delay="200">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-2xl mb-6">📈</div>
                      <h3 className="text-xl font-bold text-white mb-3">Up to 200% ROI</h3>
                      <p className="text-gray-500 text-sm">Turn a ₹15,000 wallet deposit into ₹40,000+ in retail sales. You set your own street prices and pocket every rupee of profit.</p>
                  </div>
                  <div className="feature-card p-8 rounded-3xl animate-on-scroll" data-delay="300">
                      <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-2xl mb-6">💼</div>
                      <h3 className="text-xl font-bold text-white mb-3">Prepaid Wallet</h3>
                      <p className="text-gray-500 text-sm">No monthly fees or revenue sharing. Buy inventory upfront (Questions, Premium, RDP) and spend it exactly how your customers want.</p>
                  </div>
                  <div className="feature-card p-8 rounded-3xl animate-on-scroll" data-delay="400">
                      <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-2xl mb-6">⚡</div>
                      <h3 className="text-xl font-bold text-white mb-3">Instant Provisioning</h3>
                      <p className="text-gray-500 text-sm">Access the secure Vendor Portal. Collect cash directly from your users, enter their Device ID, and instantly unlock their software.</p>
                  </div>
              </div>

              <div className="text-center animate-on-scroll" data-delay="500">
                  <a href="/partners" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-transparent border-2 border-emerald-500/50 rounded-xl hover:bg-emerald-500/10 hover:border-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                      View Wholesale Tiers & Apply &rarr;
                  </a>
              </div>
          </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-t border-white/5 bg-[#050505]">
          <div className="container mx-auto px-4 max-w-6xl">
              <h2 className="text-3xl font-bold text-white mb-12 text-center animate-on-scroll" data-delay="100">Community Feedback</h2>
              <div className="grid md:grid-cols-3 gap-6">
                  <div className="feature-card p-6 rounded-2xl animate-on-scroll" data-delay="200">
                      <p className="text-gray-400 text-sm mb-4">"The new Neural Driver is insane. I can run it completely offline on my laptop during labs without triggering any network alerts."</p>
                      <div className="text-sky-400 font-bold text-sm">- R. Mehta, B.Tech CSE</div>
                  </div>
                  <div className="feature-card p-6 rounded-2xl animate-on-scroll" data-delay="300">
                      <p className="text-gray-400 text-sm mb-4">"God Mode fixed the black screen issue I had with my university's secure browser. Now OCR works perfectly."</p>
                      <div className="text-sky-400 font-bold text-sm">- Sarah J., Med Student</div>
                  </div>
                  <div className="feature-card p-6 rounded-2xl animate-on-scroll" data-delay="400">
                      <p className="text-gray-400 text-sm mb-4">"The auto-typing speed is so natural. It handles backspaces and pauses just like a human. Helped me ace my coding interview practice."</p>
                      <div className="text-sky-400 font-bold text-sm">- Dev_Master_99</div>
                  </div>
              </div>
          </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-900 bg-black text-center">
          <div className="container mx-auto px-4 max-w-4xl">
              <h3 className="text-2xl font-bold gradient-text mb-4">CheckScript AI</h3>
              <p className="text-gray-500 text-sm mb-8">
                  Educational Use Only: This software is designed for learning assistance, self-assessment, and development testing. 
                  Users are responsible for complying with the Terms of Service of any platform they utilize.
              </p>
              <div className="flex justify-center gap-6 text-sm text-gray-400">
                  <a href="/remote" className="hover:text-white">Remote Desktop</a>
                  <a href="https://t.me/CheckScriptSupport" className="hover:text-white">Telegram Support</a>
                  <a href="/terms.html" className="hover:text-white">Terms of Use</a>
              </div>
              <div className="mt-8 text-gray-700 text-xs">
                  &copy; {new Date().getFullYear()} CheckScript AI. All Rights Reserved.
              </div>
          </div>
      </footer>
    </>
  );
};

export default CheckScriptHome;